import { clearSmartHealingOriginSession } from '../utils/smartHealingAccess';
import {
  AUTH_STORAGE_KEYS,
  deriveAdminFlag,
  parseJwt,
  persistAdminFlag,
  resolveUserNo,
} from './authShared';

const hasOwn = (target, key) => Object.prototype.hasOwnProperty.call(target, key);

const normalizeToken = (value) => {
  const token = String(value ?? '').trim();
  return token || null;
};

const normalizeUserNo = (value) => {
  const userNo = String(value ?? '').trim();
  return userNo || null;
};

export const readAuthStorage = () => {
  try {
    const accessToken = normalizeToken(window.localStorage.getItem(AUTH_STORAGE_KEYS.accessToken));
    const refreshToken = normalizeToken(window.localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken));
    const userNo = resolveUserNo({
      access_token: accessToken,
      user_no: window.localStorage.getItem(AUTH_STORAGE_KEYS.userNo),
      userNo: window.localStorage.getItem(AUTH_STORAGE_KEYS.userNoLegacy),
    });
    const isAdmin = window.localStorage.getItem(AUTH_STORAGE_KEYS.isAdmin) === 'true';
    const deviceId = normalizeToken(window.localStorage.getItem(AUTH_STORAGE_KEYS.deviceId));

    return {
      accessToken,
      refreshToken,
      userNo,
      isAdmin,
      deviceId,
    };
  } catch {
    return {
      accessToken: null,
      refreshToken: null,
      userNo: null,
      isAdmin: false,
      deviceId: null,
    };
  }
};

export const writeAuthStorage = (payload = {}) => {
  const current = readAuthStorage();

  const hasAccessToken = hasOwn(payload, 'access_token') || hasOwn(payload, 'accessToken');
  const hasRefreshToken = hasOwn(payload, 'refresh_token') || hasOwn(payload, 'refreshToken');
  const hasUserNo = hasOwn(payload, 'user_no') || hasOwn(payload, 'userNo');

  const accessToken = hasAccessToken
    ? normalizeToken(payload.access_token ?? payload.accessToken)
    : current.accessToken;
  const refreshToken = hasRefreshToken
    ? normalizeToken(payload.refresh_token ?? payload.refreshToken)
    : current.refreshToken;
  const userNo = hasUserNo
    ? normalizeUserNo(payload.user_no ?? payload.userNo)
    : (resolveUserNo({
      access_token: accessToken,
    }) || current.userNo);

  const tokenPayload = accessToken ? parseJwt(accessToken) : null;
  const adminFlag = Boolean(
    deriveAdminFlag({
      ...(tokenPayload || {}),
      ...payload,
    }),
  );

  try {
    if (accessToken) {
      window.localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, accessToken);
    } else if (hasAccessToken) {
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
    }

    if (refreshToken) {
      window.localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, refreshToken);
    } else if (hasRefreshToken) {
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
    }

    if (userNo) {
      window.localStorage.setItem(AUTH_STORAGE_KEYS.userNo, userNo);
      window.localStorage.setItem(AUTH_STORAGE_KEYS.userNoLegacy, userNo);
    } else if (hasUserNo) {
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.userNo);
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.userNoLegacy);
    }

    persistAdminFlag(adminFlag);
  } catch {
    // ignore storage failures
  }

  return readAuthStorage();
};

export const clearAuthStorage = ({ clearAppNext = true } = {}) => {
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.userNo);
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.userNoLegacy);
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.isAdmin);

    if (clearAppNext) {
      window.sessionStorage.removeItem(AUTH_STORAGE_KEYS.appNext);
    }

    clearSmartHealingOriginSession();
  } catch {
    // ignore storage failures
  }
};

export const writeAppNext = (path) => {
  try {
    const normalizedPath = String(path ?? '').trim();
    if (!normalizedPath) {
      window.sessionStorage.removeItem(AUTH_STORAGE_KEYS.appNext);
      return;
    }
    window.sessionStorage.setItem(AUTH_STORAGE_KEYS.appNext, normalizedPath);
  } catch {
    // ignore storage failures
  }
};

export const readAppNext = () => {
  try {
    return window.sessionStorage.getItem(AUTH_STORAGE_KEYS.appNext);
  } catch {
    return null;
  }
};

export const consumeAppNext = () => {
  const nextPath = readAppNext();

  try {
    window.sessionStorage.removeItem(AUTH_STORAGE_KEYS.appNext);
  } catch {
    // ignore storage failures
  }

  return nextPath;
};
